import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  Box, Card, CardContent, CardHeader,
  Typography, Stack, Divider, Grid, Button, Avatar
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import Swal from "sweetalert2";
import { AcUnit } from '@mui/icons-material'; // Generic icon for all facilities

function Roomfacilities() {
  const auth = getAuth();
  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [roomFacilities, setRoomFacilities] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => setUserData(user));
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchRole = async () => {
      if (!userData) return;
      const roleRef = doc(db, "authroles", userData.uid);
      const snap = await getDoc(roleRef);
      if (snap.exists()) setPropertyId(snap.data().propertyId || null);
    };
    fetchRole();
  }, [userData]);

  useEffect(() => {
    const fetchFacilities = async () => {
      const snap = await getDocs(collection(db, "facilities"));
      const list = snap.docs.map(d => d.data().facility);
      setFacilities(list);
    };
    fetchFacilities();
  }, []);

  useEffect(() => {
    const fetchRooms = async () => {
      if (!userData || !propertyId) return;
      const snap = await getDocs(collection(db, "rooms"));
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.userId === userData.uid && r.propertyId === propertyId);
      setRooms(list);
      setLoading(false);

      list.forEach(async room => {
        const rfDoc = doc(db, "roomfacilities", room.id);
        const rfSnap = await getDoc(rfDoc);
        setRoomFacilities(prev => ({
          ...prev,
          [room.id]: rfSnap.exists() ? rfSnap.data().facilities : []
        }));
      });
    };
    fetchRooms();
  }, [userData, propertyId]);

  const toggleFacility = (roomId, facility) => {
    const current = roomFacilities[roomId] || [];
    const updated = current.includes(facility)
      ? current.filter(f => f !== facility)
      : [...current, facility];
    setRoomFacilities(prev => ({ ...prev, [roomId]: updated }));
  };

  const saveFacilities = async (roomId) => {
    const facilitiesToSave = roomFacilities[roomId] || [];
    await setDoc(doc(db, "roomfacilities", roomId), {
      facilities: facilitiesToSave,
      roomId,
      updatedAt: new Date()
    });
    Swal.fire({
      icon: "success",
      title: "Saved!",
      text: "Room facilities updated successfully.",
      timer: 1500,
      showConfirmButton: false
    });
  };

  return (
    <Box display="flex" bgcolor="#f0f2f5" minHeight="100vh">
      <Menu />
      <Box flex={1} p={3} className="content-wrapper">
        <Typography variant="h5" fontWeight={700} mb={3}>
          🛎️ Manage Room Facilities
        </Typography>

        {loading && <Typography>Loading...</Typography>}
        {!loading && rooms.length === 0 && <Typography>No rooms found for this property</Typography>}

        <Grid className="row" spacing={3}>
          {rooms.map(room => (
            <Grid className="col-sm-12 mb-3" key={room.id}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
                  transition: "0.3s",
                  "&:hover": { transform: "translateY(-5px)", boxShadow: "0 12px 30px rgba(0,0,0,0.12)" }
                }}
              >
                <CardHeader
                  title={<Typography fontWeight={700}>{room.roomTypeName}</Typography>}
                  subheader={`Beds: ${room.bed?.count} | ${room.bed?.type}`}
                  sx={{ pb: 0 }}
                />
                <Divider sx={{ my: 1 }} />

                <CardContent>
                  <Typography fontWeight={600} mb={1}>Facilities</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    {facilities.map(facility => {
                      const selected = roomFacilities[room.id]?.includes(facility) || false;
                      return (
                        <Box
                          key={facility}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1,
                            minWidth: 120,
                            borderRadius: 2,
                            cursor: "pointer",
                            border: selected ? "2px solid #1976d2" : "1px solid #ccc",
                            background: selected ? "rgba(25, 118, 210,0.1)" : "#fff",
                            "&:hover": { transform: "scale(1.05)", transition: "0.2s" }
                          }}
                          onClick={() => toggleFacility(room.id, facility)}
                        >
                          <Avatar sx={{ bgcolor: selected ? "#1976d2" : "#e0e0e0", width: 28, height: 28, mr: 1 }}>
                            <AcUnit fontSize="small" />
                          </Avatar>
                          <Typography fontWeight={500}>{facility}</Typography>
                        </Box>
                      );
                    })}
                  </Stack>

                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => saveFacilities(room.id)}
                  >
                    Save Facilities
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

export default Roomfacilities;
